import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Camera, ImagePlus, LoaderCircle, LockKeyhole } from 'lucide-react';

interface OnboardingProps {
  initialName?: string;
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (details: { displayName: string; familyCode: string; photo: File | null }) => Promise<void>;
}

export function Onboarding({ initialName = '', isSubmitting, error, onSubmit }: OnboardingProps) {
  const [displayName, setDisplayName] = useState(initialName);
  const [familyCode, setFamilyCode] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const previewUrl = useMemo(() => photo ? URL.createObjectURL(photo) : null, [photo]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onSubmit({ displayName: displayName.trim(), familyCode: familyCode.trim(), photo });
  }

  return (
    <main className="onboarding-shell">
      <section className="onboarding-card" aria-labelledby="welcome-title">
        <div className="brand-lockup brand-lockup--centered">
          <img src="/icons/locus-icon-v2-192.png" width="68" height="68" alt="" />
          <div>
            <p className="eyebrow">Amaika · Calella</p>
            <h1 id="welcome-title">L.O.C.U.S.</h1>
          </div>
        </div>
        <p className="onboarding-card__tagline">Loved Ones Coordination &amp; Updates System</p>
        <h2>Facciamoci riconoscere</h2>
        <p className="onboarding-card__intro">Scrivi il tuo nome: se esiste già, collegheremo questo dispositivo. La foto serve solo per creare un nuovo profilo.</p>

        <form onSubmit={handleSubmit} className="onboarding-form">
          <label className="photo-picker">
            <span className={`photo-picker__preview${previewUrl ? ' photo-picker__preview--filled' : ''}`}>
              {previewUrl ? <img src={previewUrl} alt="Anteprima della tua foto" /> : <Camera size={34} aria-hidden="true" />}
            </span>
            <span className="photo-picker__copy">
              <strong>{photo ? 'Foto pronta' : 'Foto per un nuovo profilo'}</strong>
              <small>{photo ? 'Puoi sostituirla prima di entrare' : 'Opzionale se il tuo nome esiste già'}</small>
            </span>
            <ImagePlus size={22} aria-hidden="true" />
            <input
              type="file"
              accept="image/*"
              onChange={(event) => setPhoto(event.target.files?.[0] || null)}
            />
          </label>

          <label className="field">
            <span>Il tuo nome</span>
            <input
              type="text"
              autoComplete="name"
              value={displayName}
              maxLength={40}
              required
              placeholder="Es. Jessica"
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </label>

          <label className="field">
            <span>Codice famiglia</span>
            <span className="field__with-icon">
              <LockKeyhole size={19} aria-hidden="true" />
              <input
                type="password"
                autoComplete="one-time-code"
                value={familyCode}
                minLength={6}
                required
                placeholder="Inserisci il codice condiviso"
                onChange={(event) => setFamilyCode(event.target.value)}
              />
            </span>
          </label>

          {error && <p className="inline-alert inline-alert--error" role="alert">{error}</p>}

          <button className="button button--primary button--large" type="submit" disabled={isSubmitting}>
            {isSubmitting ? <><LoaderCircle className="spin" size={21} /> Accesso in corso…</> : 'Entra in L.O.C.U.S.'}
          </button>
        </form>
        <p className="privacy-note">🔒 Area privata della famiglia. Nessun tracciamento GPS.</p>
      </section>
    </main>
  );
}
